/**
 * Timestamp parsing for the verification engine — one definition, deliberately.
 *
 * ── Why this module exists ──
 *
 * `Date.parse` returns `NaN` for unparseable input, and EVERY comparison against
 * `NaN` is `false`. A time-based control written as an inline comparison therefore
 * does not fail closed on bad input — it silently skips its own branch. There is
 * no throw, no reason code and no log line: the control ceases to exist.
 *
 * That defect was found twice on the same day, in the two places it mattered most:
 *
 *   - `api-auth.ts`   — an EXPIRED API token was accepted (#46350)
 *   - `audit-room.ts` — an EXPIRED auditor grant released the scoped evidence,
 *                       and the `ok:true` payload echoed back the very expiry date
 *                       it had just ignored (#46354)
 *
 * Both were found by an adversarial reviewer, not by a gate, and the tell in each
 * case was the same: the sibling controls in the same function — revocation,
 * tenant match — fail CLOSED on identical input, because they are null and
 * equality checks. The date comparison was the only control that failed open.
 *
 * ── Why it is SHARED rather than copied ──
 *
 * The fix is four lines, which is exactly what makes copying it tempting. Two
 * copies is a coincidence; three is a pattern nobody maintains, and the fourth
 * site is where the class comes back. `packages/core/src/verification/` has ~55
 * `Date.parse` / `new Date(` call sites across ~25 modules, so this must be one
 * import, not a habit people are asked to remember.
 *
 * ── What this module does NOT do ──
 *
 * It does not validate calendars. `Date.parse("2026-02-30T00:00:00Z")` is a finite
 * number — V8 rolls the impossible day over to 2026-03-02 — so a calendar-invalid
 * timestamp parses to a real instant and this returns it. That direction is safe
 * for an expiry (it lands earlier, so it fails closed as expired) but a caller who
 * needs "is this a date a human meant" needs a stricter check than finiteness.
 * Stated here because the assumption that "obviously invalid" and "unparseable"
 * are the same set is what a first draft of the #46350 gate got wrong.
 */
/**
 * Epoch milliseconds, or `null` when `value` is not a parseable instant.
 *
 * Returning `null` rather than `NaN` is the entire point: `null` forces the caller
 * to branch, and the type stops it being fed into a comparison that would quietly
 * evaluate `false`.
 */
export function parseInstant(value) {
    if (typeof value !== "string")
        return null;
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : null;
}
/**
 * Whether `value` is a parseable instant. For guards that only need the predicate.
 */
export function isInstant(value) {
    return parseInstant(value) !== null;
}
/**
 * `true` when `now` is at or after `deadline` — and `null` when either side is
 * unreadable, which the caller MUST treat as a refusal rather than as "not yet".
 *
 * The three-valued return is the safety property. A boolean helper would collapse
 * "the deadline has not passed" and "I cannot tell whether the deadline has
 * passed" into the same permissive answer, which is the original bug wearing a
 * function's clothes.
 *
 * The comparison is `>=`: a deadline is exclusive, so a request arriving at the
 * exact expiry instant is late.
 */
export function hasReachedDeadline(now, deadline) {
    const nowMs = parseInstant(now);
    const deadlineMs = parseInstant(deadline);
    if (nowMs === null || deadlineMs === null)
        return null;
    return nowMs >= deadlineMs;
}
